import * as Notifications from "expo-notifications";
import * as Device from "expo-device";
import { Platform, Linking } from "react-native";
import { supabase } from "@/lib/supabase/client";
import Constants from "expo-constants";
import AsyncStorage from "@react-native-async-storage/async-storage";

const NOTIF_PROMPT_DISMISS_KEY = "notif_prompt_dismissed_at";
const NOTIF_PROMPT_COOLDOWN_DAYS = 90;

/**
 * Notification payload interface
 */
export interface NotificationPayload {
  title: string;
  body: string;
  data?: Record<string, unknown>;
  lottieAnimation?: string;
}

/**
 * Configure notification handler behavior
 */
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

export type NotificationPermissionStatus = "granted" | "denied" | "undetermined";

/**
 * Check current OS notification permission status without requesting
 */
export const getNotificationPermissionStatus = async (): Promise<NotificationPermissionStatus> => {
  if (!Device.isDevice) return "denied";

  try {
    const { status } = await Notifications.getPermissionsAsync();
    if (status === "granted") return "granted";
    if (status === "denied") return "denied";
    return "undetermined";
  } catch {
    return "denied";
  }
};

/**
 * Request notification permission from the OS.
 * On Android 13+, a notification channel must exist before the OS will show the popup.
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!Device.isDevice) return false;

  try {
    await setupAndroidChannels();
    const { status } = await Notifications.requestPermissionsAsync();
    return status === "granted";
  } catch {
    return false;
  }
};

/**
 * Open the OS settings app so the user can enable notifications
 */
export const openNotificationSettings = (): void => {
  Linking.openSettings();
};

/**
 * Check if the soft notification prompt was recently dismissed
 */
export const wasPromptRecentlyDismissed = async (): Promise<boolean> => {
  try {
    const dismissedAt = await AsyncStorage.getItem(NOTIF_PROMPT_DISMISS_KEY);
    if (!dismissedAt) return false;

    const daysSinceDismiss = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
    return daysSinceDismiss < NOTIF_PROMPT_COOLDOWN_DAYS;
  } catch {
    return false;
  }
};

/**
 * Mark the soft notification prompt as dismissed
 */
export const dismissNotificationPrompt = async (): Promise<void> => {
  try {
    await AsyncStorage.setItem(NOTIF_PROMPT_DISMISS_KEY, String(Date.now()));
  } catch {
    // Non-critical, ignore
  }
};

/**
 * Set up Android notification channels
 */
const setupAndroidChannels = async (): Promise<void> => {
  if (Platform.OS !== "android") return;

  await Notifications.setNotificationChannelAsync("default", {
    name: "Algemeen",
    description: "Algemene meldingen",
    importance: Notifications.AndroidImportance.MAX,
    vibrationPattern: [0, 250, 250, 250],
    lightColor: "#7C3AED",
  });

  await Notifications.setNotificationChannelAsync("chat", {
    name: "Chat berichten",
    description: "Meldingen voor nieuwe chat berichten",
    importance: Notifications.AndroidImportance.HIGH,
    vibrationPattern: [0, 100, 100, 100],
    lightColor: "#10B981",
    sound: "default",
  });

  await Notifications.setNotificationChannelAsync("games", {
    name: "Spellen",
    description: "Meldingen voor spellen en uitdagingen",
    importance: Notifications.AndroidImportance.DEFAULT,
    vibrationPattern: [0, 200, 100, 200],
    lightColor: "#F59E0B",
  });
};

/**
 * Register for push notifications and get the token.
 * Only retrieves token if permission is already granted — does NOT request permission.
 * @returns The Expo push token or null if not available
 */
export const registerForPushNotifications = async (): Promise<string | null> => {
  if (!Device.isDevice) return null;

  try {
    const status = await getNotificationPermissionStatus();
    if (status !== "granted") return null;

    const projectId = Constants.expoConfig?.extra?.eas?.projectId;
    const tokenData = await Notifications.getExpoPushTokenAsync({ projectId });

    await setupAndroidChannels();

    return tokenData.data;
  } catch (error) {
    console.error("Error registering for push notifications:", error);
    return null;
  }
};

/**
 * Save push token to database with proper upsert logic.
 * Deactivates stale tokens for the same user+platform before inserting.
 */
export const savePushToken = async (userId: string, token: string): Promise<boolean> => {
  try {
    const platform = Platform.OS;
    const deviceName = Device.deviceName || `${Device.brand} ${Device.modelName}`;

    // Deactivate ALL tokens for this user on this platform, then activate the current one
    await supabase
      .from("push_tokens")
      .update({ is_active: false })
      .eq("user_id", userId)
      .eq("platform", platform);

    // Upsert: insert or update if the token already exists
    const { error: upsertError } = await supabase
      .from("push_tokens")
      .upsert(
        {
          user_id: userId,
          token,
          platform,
          device_name: deviceName,
          is_active: true,
          last_used_at: new Date().toISOString(),
        },
        { onConflict: "token" }
      );

    if (upsertError) {
      console.error("Error upserting push token:", upsertError);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error saving push token:", error);
    return false;
  }
};

/**
 * Remove push token from database (on logout)
 * @param token - The Expo push token to remove
 */
export const removePushToken = async (token: string): Promise<boolean> => {
  try {
    await supabase
      .from("push_tokens")
      .update({ is_active: false })
      .eq("token", token);
    return true;
  } catch (error) {
    console.error("Error removing push token:", error);
    return false;
  }
};

/**
 * Schedule a local notification
 * @param notification - The notification payload
 * @param trigger - When to show the notification (null = immediately)
 */
export const scheduleLocalNotification = async (
  notification: NotificationPayload,
  trigger: Notifications.NotificationTriggerInput | null = null
): Promise<string | null> => {
  try {
    const identifier = await Notifications.scheduleNotificationAsync({
      content: {
        title: notification.title,
        body: notification.body,
        data: {
          ...notification.data,
          lottieAnimation: notification.lottieAnimation,
        },
        sound: true,
      },
      trigger,
    });

    return identifier;
  } catch (error) {
    console.error("Error scheduling notification:", error);
    return null;
  }
};

/**
 * Cancel a scheduled notification
 * @param identifier - The notification identifier
 */
export const cancelNotification = async (identifier: string): Promise<void> => {
  await Notifications.cancelScheduledNotificationAsync(identifier);
};

/**
 * Cancel all scheduled notifications
 */
export const cancelAllNotifications = async (): Promise<void> => {
  await Notifications.cancelAllScheduledNotificationsAsync();
};

/**
 * Get all scheduled notifications
 */
export const getScheduledNotifications = async () => {
  return Notifications.getAllScheduledNotificationsAsync();
};

/**
 * Create an in-app notification entry
 */
export const createInAppNotification = async ({
  userId,
  title,
  body,
  type,
  actionUrl,
  actionData,
  senderId,
  weekendId,
  eventId,
  lottieAnimation,
}: {
  userId: string;
  title: string;
  body: string;
  type: string;
  actionUrl?: string;
  actionData?: Record<string, unknown>;
  senderId?: string;
  weekendId?: string;
  eventId?: string;
  lottieAnimation?: string;
}): Promise<boolean> => {
  try {
    const { error } = await supabase.from("notifications").insert({
      user_id: userId,
      title,
      body,
      type,
      notification_type: type,
      action_url: actionUrl,
      action_data: actionData,
      sender_id: senderId,
      weekend_id: weekendId,
      event_id: eventId,
      lottie_animation: lottieAnimation,
      delivery_method: "in_app",
      is_sent: true,
      sent_at: new Date().toISOString(),
    });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error creating in-app notification:", error);
    return false;
  }
};

/**
 * Mark notification as read
 */
export const markNotificationAsRead = async (notificationId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("notifications")
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq("id", notificationId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error marking notification as read:", error);
    return false;
  }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllNotificationsAsRead = async (userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("notifications")
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("is_read", false);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    return false;
  }
};

/**
 * Get unread notification count for a user
 */
export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from("notifications")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("is_read", false);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error("Error getting unread count:", error);
    return 0;
  }
};

/**
 * Schedule an event reminder notification
 */
export const scheduleEventReminder = async ({
  eventId,
  eventTitle,
  eventStartsAt,
  minutesBefore = 15,
  userId,
}: {
  eventId: string;
  eventTitle: string;
  eventStartsAt: string;
  minutesBefore?: number;
  userId: string;
}): Promise<string | null> => {
  const eventDate = new Date(eventStartsAt);
  const reminderDate = new Date(eventDate.getTime() - minutesBefore * 60 * 1000);

  // Don't schedule if reminder would be in the past
  if (reminderDate <= new Date()) {
    return null;
  }

  // Calculate seconds until reminder
  const secondsUntil = Math.max(1, Math.floor((reminderDate.getTime() - Date.now()) / 1000));

  const identifier = await scheduleLocalNotification(
    {
      title: "Herinnering 📅",
      body: `${eventTitle} begint over ${minutesBefore} minuten`,
      data: {
        type: "event_reminder",
        eventId,
        userId,
      },
    },
    { type: "timeInterval", seconds: secondsUntil, repeats: false } as Notifications.TimeIntervalTriggerInput
  );

  return identifier;
};

/**
 * Dismiss all push notifications from the device notification tray
 * and reset the app badge count to 0
 */
export const clearDeviceNotifications = async (): Promise<void> => {
  try {
    await Notifications.dismissAllNotificationsAsync();
    await Notifications.setBadgeCountAsync(0);
  } catch (error) {
    console.error("Error clearing device notifications:", error);
  }
};

export default {
  registerForPushNotifications,
  savePushToken,
  removePushToken,
  getNotificationPermissionStatus,
  requestNotificationPermission,
  openNotificationSettings,
  wasPromptRecentlyDismissed,
  dismissNotificationPrompt,
  scheduleLocalNotification,
  cancelNotification,
  cancelAllNotifications,
  getScheduledNotifications,
  createInAppNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  getUnreadNotificationCount,
  scheduleEventReminder,
  clearDeviceNotifications,
};

